import * as Yup from 'yup';
import { AddressFormFieldNames } from 'shared/enums/addressFormFieldNames';
import { GerantFieldNames } from 'back-office/shared/components/gerant/gerantFields';
import { addressFieldWithoutPlaceId } from 'features/new-actor/form/validation-rules/addressValidationRules';
import { InputsConfig } from 'types/form/inputsConfig';
import { BrokerFormFieldNames } from './BrokerFormFieldNames';
import { BrokerFormValues } from './BrokerFormValues';
import { brokerInputsConfig } from './brokerInputsConfig';
import { brokerValidationRules } from './brokerValidationRules';

/** Address fields compared with the loaded address to detect a change. */
const ADDRESS_FIELDS = [
  AddressFormFieldNames.Address,
  AddressFormFieldNames.AdditionalAddress,
  AddressFormFieldNames.PostalCode,
  AddressFormFieldNames.City,
  AddressFormFieldNames.Country,
] as const;

/** Fields of the creation form that the update command does not carry. */
const CREATION_ONLY_FIELDS: string[] = [
  BrokerFormFieldNames.Password,
  BrokerFormFieldNames.SousNature,
  BrokerFormFieldNames.Collaborators,
  GerantFieldNames.Civility,
  GerantFieldNames.Phone,
];

/** True when the address in the form differs from the one loaded for edition. */
export const isAddressChanged = (
  values: Partial<BrokerFormValues>,
  initialValues: Partial<BrokerFormValues>
): boolean =>
  ADDRESS_FIELDS.some((field) => (values[field] ?? '').toString().trim() !== (initialValues[field] ?? '').toString().trim());

/**
 * Validation of the edit mode: the creation rules without the creation-only fields,
 * at least one plateforme (the update replaces the account's plateformes), and a Google
 * suggestion required only when the address was changed (the stored address has no
 * place_id and is kept as is when untouched).
 */
export const buildBrokerEditValidationSchema = (initialValues: Partial<BrokerFormValues>) => {
  const rules: Record<string, Yup.Schema<any>> = Object.fromEntries(
    Object.entries(brokerValidationRules).filter(([key]) => !CREATION_ONLY_FIELDS.includes(key))
  );

  rules[BrokerFormFieldNames.PlateformeIds] = Yup.array()
    .of(Yup.string().required())
    .min(1, 'Au moins une plateforme est requise');

  rules[AddressFormFieldNames.Address] = addressFieldWithoutPlaceId.test(
    'google-place-selected-when-changed',
    'Veuillez sélectionner une adresse parmi les suggestions proposées',
    function () {
      const { googlePlaceId } = this.parent;
      return !!googlePlaceId || !isAddressChanged(this.parent, initialValues);
    }
  );

  return Yup.object(rules);
};

/** Inputs of the edit mode: the plateformes become required. */
export const brokerEditInputsConfig: InputsConfig<keyof BrokerFormValues> = {
  ...brokerInputsConfig,
  [BrokerFormFieldNames.PlateformeIds]: {
    ...brokerInputsConfig[BrokerFormFieldNames.PlateformeIds],
    required: true,
  },
};
